import { transparentize } from 'polished';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ImpactDirectionDto } from 'src/easyclaim.api.client.typescript/src';
import styled, { css } from 'styled-components';

import { AppState } from '../AppState';
import { TopView, IconTrippleArrow } from '../easyclaim.app.assets/index';
import { FadeIn } from '../easyclaim.app.components/index';

import { ScreenComponent } from './ScreenComponent';

export interface ImpactDirectionsState {
	impactDirections: ImpactDirectionDto[];
}

export interface ImpactDirectionsProps extends AppState {
	clickableImpactDirections: boolean;
}

export class ImpactDirections extends ScreenComponent<
	ImpactDirectionsProps,
	ImpactDirectionsState
> {
	constructor(props: ImpactDirectionsProps) {
		super(props);
		this.state = {
			impactDirections: props.easyClaimCase?.damage?.impactDirections ?? [],
		};
	}

	arrowsDefinitions = [
		{
			direction: ImpactDirectionDto.Front,
			rot: 0,
			displayRot: 0,
			top: '0rem',
			left: 'calc(50% - 1rem)',
		},
		{
			direction: ImpactDirectionDto.FrontRight,
			rot: 30,
			displayRot: 45,
			top: '1.5rem',
			left: 'calc(100% - 4.5rem)',
		},
		{
			direction: ImpactDirectionDto.RightFront,
			rot: 60,
			displayRot: 90,
			top: 'calc(33% - 1.5rem)',
			left: 'calc(100% - 2.5rem)',
		},
		{
			direction: ImpactDirectionDto.RightRear,
			rot: 120,
			displayRot: 90,
			top: 'calc(66% - 1.5rem)',
			left: 'calc(100% - 2.5rem)',
		},
		{
			direction: ImpactDirectionDto.RearRight,
			rot: 150,
			displayRot: 135,
			top: 'calc(100% - 3.5rem)',
			left: 'calc(100% - 4.5rem)',
		},
		{
			direction: ImpactDirectionDto.Rear,
			rot: 180,
			displayRot: 180,
			top: 'calc(100% - 2rem)',
			left: 'calc(50% - 1rem)',
		},
		{
			direction: ImpactDirectionDto.RearLeft,
			rot: 210,
			displayRot: 225,
			top: 'calc(100% - 3.5rem)',
			left: '2.5rem',
		},
		{
			direction: ImpactDirectionDto.LeftRear,
			rot: 240,
			displayRot: 270,
			top: 'calc(66% - 1.5rem)',
			left: '0.5rem',
		},
		{
			direction: ImpactDirectionDto.LeftFront,
			rot: 300,
			displayRot: 270,
			top: 'calc(33% - 1.5rem)',
			left: '0.5rem',
		},
		{
			direction: ImpactDirectionDto.FrontLeft,
			rot: 330,
			displayRot: 315,
			top: '1.5rem',
			left: '2.5rem',
		},
	];

	onDirectionSelectionChanged = (impact: ImpactDirectionDto) => {
		const isNew = !this.state.impactDirections.some((x) => x === impact);

		let updated: ImpactDirectionDto[];

		if (isNew) {
			updated = [...this.state.impactDirections, impact];
		} else {
			updated = this.state.impactDirections.filter((x) => x !== impact);
		}

		this.setState(
			{
				impactDirections: updated,
			},
			() => {
				const easyClaimCase = this.props.easyClaimCase!;

				if (easyClaimCase.damage === undefined) {
					easyClaimCase.damage = {};
				}
				easyClaimCase.damage.impactDirections = updated;

				return easyClaimCase;
			}
		);
	};

	public render() {
		return (
			<div className="main-container">
				<p className="impactDirections-headline">
					<FormattedMessage id="impactDirections.title" />
					<p className="impactDirections-referenceNumber">
						{this.props.easyClaimCase?.damage?.referenceNumber}
					</p>
				</p>
				<div className="growing-container">
					<p>
						<FormattedMessage id="impactDirections.description" />
					</p>
				</div>
				<VehicleDetailsContainer>
					<DetailContainer>
						<VehicleContainer className="impact-directions-vehicle-container">
							<StyledVehicleTopView />
							{this.arrowsDefinitions.map((a, i) => (
								<StyledTrippleArrow
									key={i}
									rotation={`${a.displayRot}deg`}
									top={a.top}
									left={a.left}
									fadeInDelay={i * 0.1}
									isSelected={this.state.impactDirections.some((x) => x === a.direction)}
									onClick={() => {
										if (
											this.props.clickableImpactDirections &&
											this.props.clickableImpactDirections === true
										) {
											this.onDirectionSelectionChanged(a.direction);
										}
									}}
								/>
							))}
						</VehicleContainer>
					</DetailContainer>
				</VehicleDetailsContainer>
			</div>
		);
	}
}

const DetailContainer = styled.div`
	text-align: left;
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	margin-bottom: 1rem;
`;

const VehicleDetailsContainer = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: column;
`;

const VehicleContainer = styled.div`
	position: relative;
	margin-top: 2rem;
	margin-bottom: 2rem;
`;

const StyledVehicleTopView = styled(TopView)`
	fill: ${(p) => p.theme.colors.primary};
	color: ${(p) => p.theme.colors.primary};
	margin: 5rem 5rem;
	display: block;
`;

const StyledTrippleArrow = styled(IconTrippleArrow)<{
	isSelected: boolean;
	rotation: string;
	top?: string;
	left?: string;
	fadeInDelay: number;
}>`
	cursor: pointer;
	user-select: none;
	background: none;
	opacity: 0;
	color: ${(p) => (p.isSelected ? p.theme.colors.primary : transparentize(0.7, '#000'))};
	-webkit-tap-highlight-color: rgba(0, 0, 0, 0);
	height: 2rem;
	position: absolute;
	transition:
		color 0.3s ease-out,
		opacity 0.3s ease-out;
	transform: rotate(${(p) => p.rotation});
	${(p) => p.top && `top: ${p.top};`}
	${(p) => p.left && `left: ${p.left};`}
    animation: ${css`
		${FadeIn}
	`} 0.3s ease-in forwards ${(p) => p.fadeInDelay}s;
`;
